import {
  AggregateCardStatus,
  GetCardLinkStatusReq,
  HelcimClient,
  ListCardsOnFileReq,
} from '@kalos/kalos-rpc';
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@kalos/ui';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight as ChevronRightIcon, CreditCard, Loader2 } from 'lucide-react';
import { type FC, useCallback, useId, useMemo, useState } from 'react';

import { formatBrandLabel, pickDisplayCard } from './cardOnFileDisplay';
import { helcimCardLinkQueryKey, helcimCardsOnFileQueryKey } from './cardOnFileQueryKey';
import { HelcimCardTokenizePanel } from './HelcimCardTokenizePanel';

// Split base from hover so the non-interactive banner container can take the colors
// without a hover state it can't honor.
function aggregateTone(agg: AggregateCardStatus): { base: string; hover: string } {
  switch (agg) {
    case AggregateCardStatus.AGG_STATUS_NONE_ON_FILE:
    case AggregateCardStatus.AGG_STATUS_NOT_LINKED:
      return { base: 'border-amber-300 bg-amber-50 text-amber-950', hover: 'hover:bg-amber-100' };
    case AggregateCardStatus.AGG_STATUS_EXPIRING_SOON:
      return { base: 'border-amber-500 bg-amber-100 text-amber-950', hover: 'hover:bg-amber-200' };
    case AggregateCardStatus.AGG_STATUS_ALL_EXPIRED:
      return { base: 'border-red-300 bg-red-50 text-red-900', hover: 'hover:bg-red-100' };
    case AggregateCardStatus.AGG_STATUS_ACTIVE:
      return {
        base: 'border-emerald-300 bg-emerald-50 text-emerald-950',
        hover: 'hover:bg-emerald-100',
      };
    default:
      return { base: 'border-muted-foreground/30 bg-muted/40 text-foreground', hover: '' };
  }
}

export interface CardOnFileChipProps {
  userId: number;
  grpcBaseUrl: string;
  /**
   * 'banner' is a full-width row with the status on the left and its own action button on
   * the right; the container is inert, only the button is tappable. 'row' is the same as a
   * plain list row (label, status, a worded action) for a card of rows on phones.
   */
  variant?: 'inline' | 'block' | 'banner' | 'row';
  isMobile?: boolean;
  onTapPopulated?: () => void;
  onTokenizeComplete?: () => void;
  /**
   * When provided, fires for the empty/not-linked state instead of opening the built-in
   * tokenize dialog. Use this when the parent wants to handle card capture itself (e.g.
   * to avoid nesting a Radix Dialog inside another Radix Dialog).
   */
  onTapEmpty?: () => void;
  /**
   * When true, the empty/not-linked state renders as a red warning instead of the amber
   * nudge — used when a card is expected (e.g. tenant-occupied properties).
   */
  warnWhenEmpty?: boolean;
  /**
   * Property-level card token (Property.helcimPreferredCardToken). When it matches a saved
   * card, the chip shows that card instead of the account default — mirroring the charge
   * precedence: property card first, then account card.
   */
  overrideCardToken?: string;
}

export const CardOnFileChip: FC<CardOnFileChipProps> = ({
  userId,
  grpcBaseUrl,
  variant = 'inline',
  isMobile = false,
  onTapPopulated,
  onTokenizeComplete,
  onTapEmpty,
  warnWhenEmpty = false,
  overrideCardToken,
}) => {
  const helcimClient = useMemo(() => new HelcimClient(grpcBaseUrl), [grpcBaseUrl]);
  const [tokenizeOpen, setTokenizeOpen] = useState(false);
  const bannerLabelId = useId();

  // The chip is amber "awaiting customer" while a link is out and nothing is
  // authorized yet — poll both queries in that window so approval on the public
  // page flips the chip without a webhook.
  const { data: linkStatus } = useQuery({
    queryKey: helcimCardLinkQueryKey(userId),
    queryFn: async () =>
      (await helcimClient.getCardLinkStatus(GetCardLinkStatusReq.create({ userId }))) ?? null,
    enabled: !!userId && userId > 0,
    staleTime: 10_000,
    refetchInterval: (q) => (q.state.data?.active ? 15_000 : false),
  });
  const linkActive = !!linkStatus?.active;
  // Once the customer acted through the link (save/approve/remove), the link is
  // no longer "awaiting" anything even though it stays live.
  const linkCompleted = !!linkStatus?.completed;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: helcimCardsOnFileQueryKey(userId),
    queryFn: async () => {
      const res = await helcimClient.listCardsOnFile(ListCardsOnFileReq.create({ userId }));
      if (!res) throw new Error('Failed to list cards on file');
      return res;
    },
    enabled: !!userId && userId > 0,
    staleTime: 60_000,
    refetchInterval: (q) => {
      const anyAuth = (q.state.data?.cards ?? []).some((c) => c.authorized);
      return linkActive && !linkCompleted && !anyAuth ? 15_000 : false;
    },
  });

  const aggregate = data?.aggregateStatus ?? AggregateCardStatus.AGG_STATUS_UNSPECIFIED;
  const anyAuthorized = useMemo(() => (data?.cards ?? []).some((c) => c.authorized), [data?.cards]);
  const savedNeedsApproval = !anyAuthorized && (data?.cards ?? []).length > 0;
  const awaitingCustomer = linkActive && !linkCompleted && !anyAuthorized;
  const overrideToken = overrideCardToken?.trim() ?? '';
  const overrideCard = overrideToken
    ? (data?.cards ?? []).find((c) => c.tokenId.trim() === overrideToken)
    : undefined;
  // Green shows a card the customer actually approved; the preferred card wins
  // only among authorized ones.
  const authorizedCards = useMemo(
    () => (data?.cards ?? []).filter((c) => c.authorized),
    [data?.cards],
  );
  const displayCard =
    (overrideCard?.authorized ? overrideCard : undefined) ??
    pickDisplayCard(authorizedCards, data?.preferredCardToken);
  const noneOnFile =
    (aggregate === AggregateCardStatus.AGG_STATUS_NONE_ON_FILE ||
      aggregate === AggregateCardStatus.AGG_STATUS_NOT_LINKED) &&
    !awaitingCustomer &&
    !savedNeedsApproval;

  const handleChipClick = useCallback(() => {
    if (isLoading) return;
    if (isError) {
      void refetch();
      return;
    }
    if (noneOnFile) {
      if (onTapEmpty) {
        onTapEmpty();
      } else {
        setTokenizeOpen(true);
      }
      return;
    }
    // Awaiting-customer and approval-needed states manage through the same modal
    // as a populated chip (it shows the link banner / per-card authorize actions).
    if (displayCard || awaitingCustomer || savedNeedsApproval) {
      if (onTapPopulated) {
        onTapPopulated();
      } else if (onTapEmpty) {
        onTapEmpty();
      }
    }
  }, [
    awaitingCustomer,
    displayCard,
    isError,
    isLoading,
    noneOnFile,
    onTapEmpty,
    onTapPopulated,
    refetch,
    savedNeedsApproval,
  ]);

  const populatedLabel = useMemo(() => {
    if (!displayCard) return 'Card on file';
    const brand = formatBrandLabel(displayCard.brand);
    const exp =
      !isMobile && displayCard.expMonth > 0 && displayCard.expYear > 0
        ? ` · ${String(displayCard.expMonth).padStart(2, '0')}/${String(displayCard.expYear).slice(-2)}`
        : '';
    const propertySuffix = overrideCard && !isMobile ? ' · property card' : '';
    return `${brand} •••• ${displayCard.last4}${exp}${propertySuffix}`;
  }, [displayCard, isMobile, overrideCard]);

  const chipLabel = useMemo(() => {
    if (isLoading) return 'Card on file';
    if (isError) return 'Card — select to retry';
    if (awaitingCustomer) {
      return isMobile ? 'Link sent' : 'Link sent · Awaiting customer';
    }
    if (savedNeedsApproval) {
      return isMobile ? 'Approval needed' : 'Card saved · Approval needed';
    }
    if (noneOnFile) {
      return aggregate === AggregateCardStatus.AGG_STATUS_NOT_LINKED
        ? 'Payment profile not linked — select to add card'
        : 'No card on file · Add card';
    }
    return populatedLabel;
  }, [
    aggregate,
    awaitingCustomer,
    isError,
    isLoading,
    isMobile,
    noneOnFile,
    populatedLabel,
    savedNeedsApproval,
  ]);

  // The banner's action lives in its own button, so the label is pure status. Expiry is
  // spelled out because the banner otherwise leans on tone alone to distinguish an expired
  // card from a good one, and populatedLabel drops the date entirely on mobile.
  const bannerLabel = useMemo(() => {
    if (isLoading) return 'Card on file';
    if (isError) return 'Could not load card';
    if (awaitingCustomer) return 'Link sent · Awaiting customer';
    if (savedNeedsApproval) return 'Card saved · Approval needed';
    if (noneOnFile) {
      return aggregate === AggregateCardStatus.AGG_STATUS_NOT_LINKED
        ? 'Payment profile not linked'
        : 'No card on file';
    }
    if (aggregate === AggregateCardStatus.AGG_STATUS_ALL_EXPIRED) {
      return `${populatedLabel} · Expired`;
    }
    if (aggregate === AggregateCardStatus.AGG_STATUS_EXPIRING_SOON) {
      return `${populatedLabel} · Expiring soon`;
    }
    return populatedLabel;
  }, [
    aggregate,
    awaitingCustomer,
    isError,
    isLoading,
    noneOnFile,
    populatedLabel,
    savedNeedsApproval,
  ]);

  const chipClass =
    variant === 'block'
      ? 'w-full max-w-full justify-center px-3 py-2 text-sm font-medium'
      : 'max-w-full shrink px-2.5 py-1 text-xs font-medium sm:text-sm';

  const DANGER_TONE = { base: 'border-red-400 bg-red-50 text-red-900', hover: 'hover:bg-red-100' };
  const AWAITING_TONE = {
    base: 'border-amber-300 bg-amber-50 text-amber-950',
    hover: 'hover:bg-amber-100',
  };
  const NEEDS_APPROVAL_TONE = {
    base: 'border-muted-foreground/30 bg-muted/40 text-foreground',
    hover: 'hover:bg-muted/60',
  };

  const tone = awaitingCustomer
    ? AWAITING_TONE
    : savedNeedsApproval
      ? NEEDS_APPROVAL_TONE
      : warnWhenEmpty && noneOnFile
        ? DANGER_TONE
        : aggregateTone(aggregate);
  const colorClass = [tone.base, tone.hover].filter(Boolean).join(' ');

  const statusIcon = isLoading ? (
    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
  ) : (
    <CreditCard className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
  );

  const tokenizeDialog = !onTapEmpty && (
    <Dialog open={tokenizeOpen} onOpenChange={setTokenizeOpen}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add card on file</DialogTitle>
        </DialogHeader>
        {tokenizeOpen && (
          <HelcimCardTokenizePanel
            userId={userId}
            grpcBaseUrl={grpcBaseUrl}
            onCancel={() => setTokenizeOpen(false)}
            onDone={() => {
              setTokenizeOpen(false);
              onTokenizeComplete?.();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  if (variant === 'row') {
    const manageable =
      (!!displayCard || awaitingCustomer || savedNeedsApproval) && !!onTapPopulated;
    const showAction = isError || noneOnFile || manageable;
    const actionLabel = isError ? 'Retry' : noneOnFile ? 'Add' : 'Manage';
    const status = noneOnFile ? 'None' : bannerLabel;
    const content = (
      <>
        <span className="min-w-0 flex-1 text-sm">
          Card on file{' '}
          <span
            id={bannerLabelId}
            className={
              isError
                ? 'text-destructive'
                : noneOnFile
                  ? 'text-muted-foreground'
                  : 'text-foreground font-medium'
            }
          >
            · {status}
          </span>
        </span>
        {showAction && (
          <span className="flex shrink-0 items-center gap-1 text-sm font-medium">
            {actionLabel}
            <ChevronRightIcon className="h-4 w-4" aria-hidden />
          </span>
        )}
      </>
    );
    return (
      <>
        {showAction ? (
          <button
            type="button"
            onClick={handleChipClick}
            disabled={isLoading}
            aria-describedby={bannerLabelId}
            className="active:bg-muted flex min-h-12 w-full items-center gap-3 px-4 py-2 text-left"
          >
            {content}
          </button>
        ) : (
          <div className="flex min-h-12 w-full items-center gap-3 px-4 py-2">{content}</div>
        )}
        {tokenizeDialog}
      </>
    );
  }

  if (variant === 'banner') {
    // Hidden when a tap would no-op: nothing to retry or add, and either no card to manage
    // or no handler to manage it with.
    const manageable =
      (!!displayCard || awaitingCustomer || savedNeedsApproval) && !!onTapPopulated;
    const showAction = isError || noneOnFile || manageable;
    const actionLabel = isError ? 'Retry' : noneOnFile ? 'Add card' : 'Manage';
    // On error the aggregate is UNSPECIFIED, whose neutral tone reads as "no information"
    // rather than as a failure.
    const bannerTone = isError ? DANGER_TONE.base : tone.base;

    return (
      <>
        <div
          className={`flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 ${bannerTone}`}
        >
          <div className="flex min-w-0 items-center gap-1.5">
            {statusIcon}
            <span id={bannerLabelId} className="min-w-0 truncate text-sm font-medium">
              {bannerLabel}
            </span>
          </div>
          {showAction && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 shrink-0 bg-white px-2.5 text-xs"
              onClick={handleChipClick}
              disabled={isLoading}
              // The button's own name is just "Manage"/"Add card"; point at the status text
              // so a screen reader hears which card it acts on.
              aria-describedby={bannerLabelId}
            >
              {actionLabel}
            </Button>
          )}
        </div>

        {tokenizeDialog}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleChipClick}
        disabled={isLoading}
        className={
          variant === 'block'
            ? 'inline-flex w-full min-w-0'
            : 'inline-flex min-w-0 max-w-full shrink-0'
        }
      >
        <Badge
          variant="outline"
          className={`${colorClass} ${chipClass} flex cursor-pointer items-center gap-1.5 border text-left transition-colors`}
        >
          {statusIcon}
          <span className="min-w-0 truncate">{chipLabel}</span>
        </Badge>
      </button>

      {tokenizeDialog}
    </>
  );
};
